import { MAX_BUCKETS } from '@omnea-app/packages/universal/reporting/widgetLimits'
import { NUMERIC_BUCKET_WIDTHS, numericBucketMember } from './bucketing'
import { type InsightsFilter } from './cubeQuery'

const BEST_FIT_TARGET = 12

const EXPONENT_FORM = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/

/**
 * The number's shortest decimal representation, with exponent forms expanded ('1e-7' → '0.0000001').
 *
 * This is the wire format for Cube filter bounds and the input to the scaling below, so it has to be
 * locale-independent ASCII: String(), never Intl.NumberFormat, whose minus sign and digit set are
 * whichever ones the reader's locale uses.
 */
export const toDecimalString = (value: number): string => {
  const shortest = String(value)
  const match = EXPONENT_FORM.exec(shortest)
  if (!match) return shortest

  const [, sign = '', intPart = '', fracPart = '', exponent = '0'] = match
  const digits = `${intPart}${fracPart}`
  const pointPosition = intPart.length + Number(exponent)

  if (pointPosition <= 0) return `${sign}0.${'0'.repeat(-pointPosition)}${digits}`
  if (pointPosition >= digits.length) return `${sign}${digits}${'0'.repeat(pointPosition - digits.length)}`
  return `${sign}${digits.slice(0, pointPosition)}.${digits.slice(pointPosition)}`
}

const decimals = (value: number): number => {
  const text = toDecimalString(value)
  const point = text.indexOf('.')
  return point === -1 ? 0 : text.length - point - 1
}

// Postgres `numeric` is exact and JS doubles are not: 0.3 / 0.1 floors to 2 in doubles and 3 in
// Postgres, so a client index computed in doubles disagrees with the rows Cube grouped. Every
// operand is scaled to an integer through its decimal string — `value * scale` is itself inexact —
// and divided back only at the boundary. BigInt because 10,000,000 at eight fraction digits
// exceeds 2^53.
const scaled = (value: number, fractionDigits: number): bigint => {
  const text = toDecimalString(value)
  const negative = text.startsWith('-')
  const [integer = '', fraction = ''] = (negative ? text.slice(1) : text).split('.')
  const padded = `${fraction}${'0'.repeat(fractionDigits)}`.slice(0, fractionDigits)
  const magnitude = BigInt(`${integer}${padded}`)
  return negative ? -magnitude : magnitude
}

const floorDiv = (dividend: bigint, divisor: bigint): bigint => {
  const quotient = dividend / divisor
  const truncatedTowardZero = dividend % divisor !== 0n && dividend < 0n !== divisor < 0n
  return truncatedTowardZero ? quotient - 1n : quotient
}

// Scaling by decimals(w) alone is not enough: Min 0.015 at w 0.01 must give index 1, and
// 0.015 × 100 is not an integer.
const commonScale = (...values: number[]): number => Math.max(...values.map(decimals))

const exactBucketIndex = (value: number, w: number): bigint => {
  const fractionDigits = commonScale(value, w)
  return floorDiv(scaled(value, fractionDigits), scaled(w, fractionDigits))
}

export const bucketIndex = (value: number, w: number): number => Number(exactBucketIndex(value, w))

export const bucketEdge = (index: number, w: number): number => {
  const fractionDigits = decimals(w)
  return Number(BigInt(index) * scaled(w, fractionDigits)) / 10 ** fractionDigits
}

// Subtracted in bigint: two indices past 2^53 round to the same double, so narrowing first destroys
// the difference and an oversized range reads as one bucket — the case MAX_BUCKETS exists to reject.
export const inRangeCount = (min: number, max: number, w: number): number =>
  Number(exactBucketIndex(max, w) - exactBucketIndex(min, w) + 1n)

export type BucketRange = { min: number; max: number; dataMin: number; dataMax: number }

// A bound that excludes data as of the last probe draws one extra bar for the excluded side.
export const totalBuckets = ({ min, max, dataMin, dataMax }: BucketRange, w: number): number =>
  inRangeCount(min, max, w) + (min > dataMin ? 1 : 0) + (max < dataMax ? 1 : 0)

export const isStopEnabled = (range: BucketRange, w: number): boolean => totalBuckets(range, w) <= MAX_BUCKETS

export const enabledStops = (range: BucketRange): number[] => NUMERIC_BUCKET_WIDTHS.filter((w) => isStopEnabled(range, w))

export const bestFitWidth = (range: BucketRange): number | null => {
  // The ladder is ascending and the comparison is strict, so a tie falls to the smaller width.
  return enabledStops(range).reduce<number | null>((best, w) => {
    if (best === null) return w
    return Math.abs(totalBuckets(range, w) - BEST_FIT_TARGET) < Math.abs(totalBuckets(range, best) - BEST_FIT_TARGET) ? w : best
  }, null)
}

export const bucketLabelBounds = (index: number, w: number, min?: number, max?: number): { lo: number; hi: number } => ({
  lo: min === undefined ? bucketEdge(index, w) : Math.max(bucketEdge(index, w), min),
  hi: max === undefined ? bucketEdge(index + 1, w) : Math.min(bucketEdge(index + 1, w), max),
})

/** The contract pairs a field's alias stem with the raw member on the same join path. */
export type NumericBucketField = { stem: string; rawMember: string }

export type NumericBucketGrouping = { w: number; min?: number; max?: number }

/**
 * What a bucketed breakdown asks Cube for: the width's bucket dimension, plus the raw member bounded
 * inclusively to [min, max] and restricted to rows that answered at all, so a null answer is not
 * drawn as its own bar.
 */
export const numericBucketQuery = (
  { stem, rawMember }: NumericBucketField,
  { w, min, max }: NumericBucketGrouping
): { dimension: string; filters: InsightsFilter[] } => ({
  dimension: numericBucketMember(stem, w),
  filters: [
    { dimension: rawMember, operator: 'set' },
    ...(min === undefined ? [] : [{ dimension: rawMember, operator: 'gte' as const, values: [toDecimalString(min)] }]),
    ...(max === undefined ? [] : [{ dimension: rawMember, operator: 'lte' as const, values: [toDecimalString(max)] }]),
  ],
})

export type BucketOverflowSide = 'under' | 'over'

/**
 * The one condition selecting what the bars exclude on `side`. A single inverted bound and nothing
 * else: paired with the in-range bounds it would ask for v ≥ Min and v < Min, and `set` is implied
 * because a comparison never matches null.
 */
export const numericBucketOverflowFilter = (
  { rawMember }: NumericBucketField,
  side: BucketOverflowSide,
  bound: number
): InsightsFilter => ({
  dimension: rawMember,
  operator: side === 'under' ? 'lt' : 'gt',
  values: [toDecimalString(bound)],
})
